import React, { useEffect } from 'react';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  cn,
  LeftCard,
  RightCard,
  TextSeparator,
} from '@sandtech-hub-monorepo/ui-kit';

import {
  signUpTitle as defaultSignUpTitle,
  signUpCTAQuestion,
  signUpCTAText,
} from '../../../../stringsManager';
import { SignUpForm } from './components/SignUpForm';
import { socialGridColumnsClassName } from '../socialButtonsGrid';
import {
  RefereeBottomCard,
  RefereeLeftCard,
} from './components/RefereeLeftCard';
import {
  SocialProviderType,
  useCognito,
} from '@sandtech-hub-monorepo/ui-auth-kit';
import { format } from 'date-fns';

export interface SignUpProps {
  loginRoute: string;
  confirmSignUpRoute: string;
  supportRoute?: string;
  signUpCover: string;
  signUpWithGoogleLogo?: React.ReactNode;
  signUpWithFacebookLogo?: React.ReactNode;
  signUpWithLinkedInLogo?: React.ReactNode;
  signUpWithEhubLogo?: React.ReactNode;
  coverLogo?: React.ReactNode;
  coverTitle?: string;
  coverSubtitle?: string;
  signUpTitle?: string;
  /**
   * Restyles the title. Defaults to today's plain
   * `text-foreground text-2xl font-medium text-center mt-6 lg:mt-0`, so
   * every existing caller is unaffected.
   */
  titleClassName?: string;
  privacyPolicyLink?: string;
  termsOfServiceLink?: string;
  recaptchaSiteKey?: string;
  brandName?: string;
  /**
   * Replaces the default per-provider social button styling with one shared
   * class, so every provider (including eHub) renders identically.
   */
  socialButtonsClassName?: string;
  /**
   * Adds a dark gradient scrim over signUpCover, for photos that don't have
   * enough natural contrast for a light logo/title.
   */
  overlay?: boolean;
  overlayClassName?: string;
  /**
   * Anchors coverLogo to the top or bottom of the cover. Defaults to the top.
   */
  coverLogoPosition?: 'top' | 'bottom';
  /**
   * Renders a compact solid-color banner carrying coverLogo, visible only
   * below `lg` (where the cover photo is otherwise hidden entirely).
   * Undefined (the default) renders nothing, matching every existing
   * caller's current mobile behavior. Has no effect when
   * showRefereeMarketing is active (RefereeLeftCard doesn't support it).
   */
  coverMobileHeaderClassName?: string;
  /**
   * Overrides the grid-cols-* class the social button grid picks by count
   * (e.g. for a responsive 2-cols-on-mobile/4-on-desktop layout). Undefined
   * keeps today's single fixed column count for every existing caller.
   */
  socialGridClassName?: string;
  /**
   * Extra classes on RightCard's inner content column. Passed through to
   * RightCard - see its prop doc. Defaults to nothing extra, so every
   * existing caller keeps today's exact layout.
   */
  rightCardClassName?: string;
  /**
   * Renders the ALX referral marketing panels when the URL carries a `ref`
   * code. Defaults to true; pass false on brands that have no referral
   * programme, so the normal cover is used instead. Does not affect the
   * `custom:referredBy` attribute, which is written either way.
   */
  showRefereeMarketing?: boolean;
  onReferralPageView?: () => void;
  onSignUpSubmit?: () => void;
  /** Overrides the "Social sign up options" separator text. */
  socialSeparatorText?: string;
  /** Overrides the "Manual sign up" separator text. */
  formSeparatorText?: string;
  /**
   * Hides the First Name/Last Name fields. Passed through to SignUpForm -
   * see its prop doc for what this means for Cognito's required
   * given_name/family_name attributes. Defaults to false, so every
   * existing caller is unaffected.
   */
  hideNameFields?: boolean;
  /**
   * Adds a "Confirm password" field. Passed through to SignUpForm.
   * Defaults to false, so every existing caller is unaffected.
   */
  showConfirmPassword?: boolean;
  /**
   * Hides the marketing-communications consent checkbox. Passed through to
   * SignUpForm. Defaults to false, so every existing caller is unaffected.
   */
  hideMarketingConsent?: boolean;
  /**
   * Restyles the terms-of-service/privacy-policy links. Passed through to
   * SignUpForm. Defaults to today's plain underline for every existing
   * caller.
   */
  legalLinkClassName?: string;
  /**
   * Replaces "{brandName}'s" before "privacy policy". Passed through to
   * SignUpForm. Defaults to `${brandName}'s` for every existing caller.
   */
  privacyPolicyPrefix?: string;
  /** The "Already have an account?" row - question text. */
  ctaClassName?: string;
  /** The "Already have an account?" row - "Log in" link. */
  ctaLinkClassName?: string;
  /** Overrides the "Log in" link text (e.g. "Sign in here"). */
  ctaLinkText?: string;
  /** The "Struggling to log in..." row - question text. */
  supportClassName?: string;
  /** The "Struggling to log in..." row - "Click here to contact us" line. */
  supportBodyClassName?: string;
  /** The "Struggling to log in..." row - "Click here" link. */
  supportLinkClassName?: string;
}

export const SignUp = (props: SignUpProps) => {
  const navigate = useNavigate();
  const { loading, signUp, socialSignIn } = useCognito();
  const [params] = useSearchParams();
  const referralCode = params.get('ref')?.trim();

  useEffect(() => {
    if (referralCode) props.onReferralPageView?.();
    // intentionally runs once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignUp = async (values: {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    marketingConsent?: boolean;
    recaptchaToken?: string;
  }) => {
    // Cognito's given_name/family_name are `required` on the User Pool and
    // reject an empty string outright ("Attributes did not conform to the
    // schema"), so hideNameFields (no input to collect either from) can't
    // just send ''. A placeholder derived from the email stands in until a
    // later, blocking onboarding step overwrites both via
    // updateUserAttributes (both are `mutable: true`).
    const givenName = values.firstName || values.email.split('@')[0] || 'User';
    const familyName = values.lastName || 'Pending';

    const signUpResult = await signUp({
      username: values.email,
      password: values.password,
      options: {
        userAttributes: {
          email: values.email,
          given_name: givenName,
          family_name: familyName,
          ...(referralCode && { 'custom:referredBy': referralCode }),
          'custom:agreedTosAt': format(new Date(), 'yyyy-MM-dd'),
          ...(values.marketingConsent && {
            'custom:agreedToComsAt': format(new Date(), 'yyyy-MM-dd'),
          }),
        },
        ...(values.recaptchaToken && {
          clientMetadata: {
            recaptchaToken: values.recaptchaToken,
          },
        }),
        autoSignIn: true,
      },
    });

    if (signUpResult) {
      props.onSignUpSubmit?.();

      if (signUpResult.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        params.set('email', encodeURIComponent(values.email));
        // Replace: the form is spent once the account exists, and Back from
        // the confirmation page must not return to it.
        navigate(
          {
            pathname: props.confirmSignUpRoute,
            search: params.toString(),
          },
          { replace: true }
        );
      }
    }
  };

  const handleGoogleSignUp = async () => {
    socialSignIn('Google');
  };

  const handleFacebookSignIn = () => {
    socialSignIn('Facebook');
  };

  const handleLinkedInSignIn = () => {
    socialSignIn({ custom: 'LinkedIn' });
  };

  const handleEhubSignUp = () => {
    socialSignIn({ custom: 'Ehub' } as SocialProviderType);
  };

  const socialLogos = [
    props.signUpWithGoogleLogo,
    props.signUpWithLinkedInLogo,
    props.signUpWithFacebookLogo,
    props.signUpWithEhubLogo,
  ].filter((x) => !!x);
  const hasSocialSignUp = socialLogos.length > 0;
  const socialButtonClassName = cn(
    'w-full',
    props.socialButtonsClassName ?? 'bg-background text-gray-700 border'
  );
  const ehubButtonClassName = cn(
    'w-full',
    props.socialButtonsClassName ??
      'border-[rgb(2,157,224)] bg-transparent text-[rgb(2,157,224)] hover:text-white border active:text-white active:bg-[rgb(5,140,224)] hover:bg-[rgb(2,157,224)]'
  );

  const showRefereeMarketing =
    (props.showRefereeMarketing ?? true) && !!referralCode;

  return (
    <>
      {showRefereeMarketing ? (
        <RefereeLeftCard />
      ) : (
        <LeftCard
          backgroundImage={props.signUpCover}
          logo={props.coverLogo}
          title={props.coverTitle}
          subtitle={props.coverSubtitle}
          overlay={props.overlay}
          overlayClassName={props.overlayClassName}
          logoPosition={props.coverLogoPosition}
          mobileHeaderClassName={props.coverMobileHeaderClassName}
        />
      )}
      <RightCard className={props.rightCardClassName}>
        <div
          className={cn(
            'mx-auto flex flex-col justify-center space-y-5 w-[80vw] max-w-[80vw] sm:w-96 sm:max-w-sm lg:w-96 lg:max-w-sm',
            showRefereeMarketing && 'pb-4 lg:pb-0'
          )}
        >
          <h1
            className={cn(
              'text-foreground text-2xl font-medium text-center mt-6 lg:mt-0',
              props.titleClassName
            )}
          >
            {props.signUpTitle ? props.signUpTitle : defaultSignUpTitle}
          </h1>
          {hasSocialSignUp && (
            <TextSeparator
              text={props.socialSeparatorText ?? 'Social sign up options'}
              className="font-semibold"
            />
          )}
          {hasSocialSignUp && (
            <div
              className={cn(
                'grid gap-4',
                socialGridColumnsClassName(socialLogos.length),
                props.socialGridClassName
              )}
            >
              {props.signUpWithGoogleLogo && (
                <Button
                  onClick={handleGoogleSignUp}
                  className={socialButtonClassName}
                  disabled={loading}
                >
                  {props.signUpWithGoogleLogo}
                </Button>
              )}
              {props.signUpWithLinkedInLogo && (
                <Button
                  onClick={handleLinkedInSignIn}
                  className={socialButtonClassName}
                  disabled={loading}
                >
                  {props.signUpWithLinkedInLogo}
                </Button>
              )}
              {props.signUpWithFacebookLogo && (
                <Button
                  onClick={handleFacebookSignIn}
                  className={socialButtonClassName}
                  disabled={loading}
                >
                  {props.signUpWithFacebookLogo}
                </Button>
              )}
              {props.signUpWithEhubLogo && (
                <Button
                  onClick={handleEhubSignUp}
                  className={ehubButtonClassName}
                  disabled={loading}
                >
                  {props.signUpWithEhubLogo}
                </Button>
              )}
            </div>
          )}
          {hasSocialSignUp && (
            <TextSeparator
              text={props.formSeparatorText ?? 'Manual sign up'}
              className="font-semibold"
            />
          )}
          <SignUpForm
            privacyPolicyLink={
              props.privacyPolicyLink ??
              'https://www.alxafrica.com/learner-privacy-policy-2/'
            }
            termsOfServiceLink={
              props.termsOfServiceLink ??
              'https://www.alxafrica.com/terms-conditions-portal/'
            }
            loading={loading}
            onSubmit={handleSignUp}
            recaptchaSiteKey={props.recaptchaSiteKey}
            brandName={props.brandName ?? 'ALX'}
            hideNameFields={props.hideNameFields}
            showConfirmPassword={props.showConfirmPassword}
            hideMarketingConsent={props.hideMarketingConsent}
            legalLinkClassName={props.legalLinkClassName}
            privacyPolicyPrefix={props.privacyPolicyPrefix}
          />
          <div className="flex gap-1 justify-center">
            <p
              className={cn(
                'text-sm text-muted-foreground',
                props.ctaClassName
              )}
            >
              {signUpCTAQuestion}
            </p>
            <Link
              to={`${props.loginRoute}${
                params.size > 0 ? '?' + params.toString() : ''
              }`}
              className={cn('text-sm text-primary', props.ctaLinkClassName)}
            >
              {props.ctaLinkText ?? signUpCTAText}
            </Link>
          </div>
          {props.supportRoute ? (
            <div className="flex flex-col items-center text-sm">
              <p
                className={cn(
                  'text-card-foreground font-medium',
                  props.supportClassName
                )}
              >
                Struggling to log in or sign up?
              </p>
              <p
                className={cn(
                  'text-muted-foreground',
                  props.supportBodyClassName
                )}
              >
                <Link
                  to={props.supportRoute}
                  className={cn(
                    'text-sm text-primary leading-tight',
                    props.supportLinkClassName
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click here
                </Link>{' '}
                to contact us
              </p>
            </div>
          ) : null}
        </div>
      </RightCard>
      {showRefereeMarketing && <RefereeBottomCard />}
    </>
  );
};
